import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { useFormState } from 'informed'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'

import query from 'config/localServer/query/priceLevelsQuery'
import { loadAppConstants } from 'gql/query'
import { Number, Select } from '../'
import { StyledContainer, StyledCol, Dot, Tooltip } from './styled'

const MWStEnums = ['normal', 'reduced', 'free', 'special']
const defaultPrecision = 2
const getPrecisions = vals => () => vals.map(val => {
	const decPart = (val + '').split('.')[1]
	return decPart ? decPart.length <= defaultPrecision ? defaultPrecision : decPart.length : defaultPrecision
})

const PriceForm = ({ disabledFields = [], enableNegative, priceFieldsProps }) => {
	const [t, i18n] = useTranslation()
	const { data } = useQuery(query, {
		fetchPolicy: 'cache-and-network'
	})
	const { values } = useFormState()
	const normalPrices = values?.price || []
	const inCafePrices = values?.priceInCafe || []
	const normalPrecisions = useMemo(getPrecisions(normalPrices), [JSON.stringify(normalPrices)])
	const inCafePrecisions = useMemo(getPrecisions(inCafePrices), [JSON.stringify(inCafePrices)])
	const activeLevels = get(data, 'getClientPriceLevels', [])
	const isFieldDisabled = field => disabledFields.some(item => item === field)
	const getToolTip = (text, dotColor) => (
		<Tooltip>
			<Dot color={dotColor} />
			<span>{text}</span>
		</Tooltip>
	)
	const outPriceTooltip = getToolTip(t('product_section.strings.price_out_cafe'), '#c13c44')
	const inPriceTooltip = getToolTip(t('product_section.strings.price_in_cafe'), '#3cb950')
	const taxRemoteOptions = {
		query: loadAppConstants,
		variables: { type: 33, language: i18n.language },
		modifyOptions: options => options.map(item => ({ ...item, value: MWStEnums[item.value - 1] }))
	}

	return(
		<StyledContainer>
			{activeLevels.map((item, i) => (
				<StyledCol key={i}>
					<Number
						field={`price[${i}]`}
						label={item || `${t('product_section.strings.price')} ${i + 1}`}
						placeholder='Normal'
						format='currency'
						tooltip={outPriceTooltip}
						fieldIco={<Dot color='#c13c44' />}
						fieldIcoPos='right'
						withControls={false}
						disabled={isFieldDisabled(`price[${i}]`)}
						enableNegative={enableNegative}
						shrink
						toFixed={normalPrecisions[i] || defaultPrecision}
						precision={normalPrecisions[i] || defaultPrecision}
						keepState
						{...priceFieldsProps && { ...priceFieldsProps }} />
					<Number
						field={`priceInCafe[${i}]`}
						placeholder='Im Haus'
						format='currency'
						tooltip={inPriceTooltip}
						fieldIco={<Dot color='#3cb950' />}
						fieldIcoPos='right'
						withControls={false}
						disabled={isFieldDisabled(`priceInCafe[${i}]`)}
						enableNegative={enableNegative}
						shrink
						toFixed={inCafePrecisions[i] || defaultPrecision}
						precision={inCafePrecisions[i] || defaultPrecision}
						keepState
						{...priceFieldsProps && { ...priceFieldsProps }} />
				</StyledCol>
			))}
			<StyledCol>
				<Select
					field='codeMWSt'
					label={t('product_section.fields.tax')}
					remoteOptions={taxRemoteOptions}
					disabled={isFieldDisabled('codeMWSt')}
					tooltip={getToolTip('MwSt Außer Haus', '#c13c44')}
					fieldIco={<Dot color='#c13c44' />}
					fieldIcoPos='left'
					styles={{ control: controlStyles => ({ ...controlStyles, paddingLeft: '12px' }) }}
					shrink />
				<Select
					field='codeMWSt2'
					remoteOptions={taxRemoteOptions}
					disabled={isFieldDisabled('codeMWSt2')}
					tooltip={getToolTip('MwSt Im Haus', '#3cb950')}
					fieldIco={<Dot color='#3cb950' />}
					fieldIcoPos='left'
					styles={{ control: controlStyles => ({ ...controlStyles, paddingLeft: '12px' }) }}
					shrink />
			</StyledCol>
		</StyledContainer>
	)
}

PriceForm.propTypes = {
	disabledFields: PropTypes.array,
	enableNegative: PropTypes.bool,
	priceFieldsProps: PropTypes.object
}

PriceForm.defaultProps = {
	enableNegative: false
}

export default PriceForm
